import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { DashboardLayout } from "../../components/dashboard-layout";

const apiUrl = `${process.env.REACT_APP_API_URL}/dg2-admin/partners`;

const authConfig = () => {
    const token = localStorage.getItem("user");
    if (!token) throw new Error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
    return { headers: { Authorization: `Bearer ${token}` } };
};

const getErrorMessage = (error) =>
    error.response?.data?.error?.message || error.message || "Đã xảy ra lỗi khi thao tác.";

function Dg2Partners() {
    const [partners, setPartners] = useState([]);
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [issuedKey, setIssuedKey] = useState(null);
    const [copyMessage, setCopyMessage] = useState("");

    const fetchPartners = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(apiUrl, authConfig());
            setPartners(response.data?.data || []);
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPartners();
    }, [fetchPartners]);

    const createPartner = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const response = await axios.post(apiUrl, { name: name.trim() }, authConfig());
            setIssuedKey(response.data.data);
            setName("");
            await fetchPartners();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setSaving(false);
        }
    };

    const rotateKey = async (partner) => {
        const confirmed = await swal({
            title: "Đổi API key?",
            text: `Key hiện tại của "${partner.name}" sẽ bị vô hiệu ngay lập tức.`,
            icon: "warning",
            buttons: ["Hủy", "Đổi key"],
            dangerMode: true,
        });
        if (!confirmed) return;

        setError("");
        try {
            const response = await axios.post(
                `${apiUrl}/${partner.partnerId}/rotate-key`,
                {},
                authConfig()
            );
            setIssuedKey(response.data.data);
            await fetchPartners();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        }
    };

    const togglePartner = async (partner) => {
        const nextActive = !partner.isActive;
        const confirmed = await swal({
            title: nextActive ? "Kích hoạt đối tác?" : "Tạm khóa đối tác?",
            text: nextActive
                ? `Cho phép "${partner.name}" sử dụng API DG2 trở lại?`
                : `Vô hiệu hóa key của "${partner.name}" ngay bây giờ?`,
            icon: "warning",
            buttons: ["Hủy", nextActive ? "Kích hoạt" : "Tạm khóa"],
            dangerMode: !nextActive,
        });
        if (!confirmed) return;

        setError("");
        try {
            await axios.patch(
                `${apiUrl}/${partner.partnerId}`,
                { isActive: nextActive },
                authConfig()
            );
            await fetchPartners();
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        }
    };

    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText(issuedKey.apiKey);
            setCopyMessage("Đã sao chép key.");
        } catch (copyError) {
            setCopyMessage("Không thể tự sao chép. Hãy chọn và sao chép key thủ công.");
        }
    };

    const closeKeyDialog = () => {
        setIssuedKey(null);
        setCopyMessage("");
    };

    return (
        <DashboardLayout>
            <Box component="main" sx={{ py: 4, backgroundColor: "#F5F8FA", minHeight: "100vh" }}>
                <Container maxWidth="lg">
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h5" fontWeight={800}>Quản lý API key DG2</Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                Key chỉ hiển thị một lần khi tạo mới hoặc đổi key.
                            </Typography>
                        </Box>

                        {error && <Alert severity="error">{error}</Alert>}

                        <Paper component="form" onSubmit={createPartner} sx={{ p: 3 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                                <TextField
                                    label="Tên đối tác"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    inputProps={{ maxLength: 80 }}
                                    required
                                    fullWidth
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={saving || !name.trim()}
                                    sx={{ minWidth: 150, height: 56 }}
                                >
                                    {saving ? <CircularProgress size={22} color="inherit" /> : "Tạo đối tác"}
                                </Button>
                            </Stack>
                        </Paper>

                        <Paper sx={{ overflowX: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tên đối tác</TableCell>
                                        <TableCell>Mã đối tác</TableCell>
                                        <TableCell>Ngày tạo</TableCell>
                                        <TableCell>Trạng thái</TableCell>
                                        <TableCell align="right">Thao tác</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <CircularProgress size={24} />
                                            </TableCell>
                                        </TableRow>
                                    ) : partners.length ? partners.map((partner) => (
                                        <TableRow key={partner.partnerId}>
                                            <TableCell>{partner.name}</TableCell>
                                            <TableCell sx={{ fontFamily: "monospace" }}>{partner.partnerId}</TableCell>
                                            <TableCell>
                                                {partner.createdAt
                                                    ? new Date(partner.createdAt).toLocaleString("vi-VN")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={partner.isActive ? "success" : "default"}
                                                    label={partner.isActive ? "Đang hoạt động" : "Đã khóa"}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        startIcon={<AutorenewIcon />}
                                                        onClick={() => rotateKey(partner)}
                                                    >
                                                        Đổi key
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color={partner.isActive ? "error" : "success"}
                                                        onClick={() => togglePartner(partner)}
                                                    >
                                                        {partner.isActive ? "Tạm khóa" : "Kích hoạt"}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                Chưa có đối tác DG2.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Stack>
                </Container>
            </Box>

            <Dialog open={Boolean(issuedKey)} onClose={closeKeyDialog} fullWidth maxWidth="sm">
                <DialogTitle>API key mới</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Hãy lưu key này ngay. Sau khi đóng hộp thoại, key không thể xem lại; đổi key sẽ vô hiệu hóa key cũ.
                    </Alert>
                    <TextField
                        label={`Key của ${issuedKey?.name || "đối tác"}`}
                        value={issuedKey?.apiKey || ""}
                        fullWidth
                        InputProps={{ readOnly: true }}
                    />
                    {copyMessage && <Typography variant="body2" sx={{ mt: 1 }}>{copyMessage}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<ContentCopyIcon />} onClick={copyKey}>Sao chép</Button>
                    <Button onClick={closeKeyDialog}>Đã lưu key</Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default Dg2Partners;
